import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import orderServices from "../../services/orders/orderservice";
import { POSModel } from "../../models/orders/order-list-model";
import posService from "../../services/pos/pos.service";

interface OutwardChallanSearchProps {
  setLoading: (loading: boolean) => void;
  onSearch: (data: any[], challanCount: number) => void; 
  onDateRangeChange: (start: Date | null, end: Date | null) => void;
}

const OutwardChallanSearch = forwardRef<unknown, OutwardChallanSearchProps>(
  ({ setLoading, onSearch, onDateRangeChange  }, ref) => {
    const toast = useRef<Toast>(null);
    const [posList, setPosList] = useState<POSModel[]>([]);
    const [selectedPosId, setSelectedPosId] = useState<number | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
      const fetchPOS = async () => {
        try {
          const response = await orderServices.getPOS();
          setPosList(response);
          if (response.length === 1) {
            setSelectedPosId(response[0].posID);
          }
        } catch {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load POS data.",
          });
        }
      };
      fetchPOS();
    }, []);

    const handleSearch = async () => {
      if (!selectedPosId) {
        toast.current?.show({
          severity: "warn",
          summary: "Validation",
          detail: "Please select the POS",
        });
        return;
      }

      if (!startDate || !endDate) {
        toast.current?.show({
          severity: "warn",
          summary: "Validation",
          detail: "Please select Start and End dates",
        });
        return;
      }

      setLoading(true);
      const adjustedStartDate = new Date(startDate);
      adjustedStartDate.setHours(0, 0, 0, 0);

      const adjustedEndDate = new Date(endDate);
      adjustedEndDate.setHours(23, 59, 59, 999);

      const formatToSQLDateTime = (date: Date) =>
        date.toLocaleString("sv-SE").replace(" ", "T");

      const payload = {
        posId: selectedPosId,
        startDate: formatToSQLDateTime(adjustedStartDate),
        endDate: formatToSQLDateTime(adjustedEndDate),
      };

      try {
        const result = await posService.GetPOSOutwardChallan(payload);
        if (result?.movemementresult) {
          onSearch(result.movemementresult, result.challanCount ?? 0);
        } else {
          toast.current?.show({
            severity: "info",
            summary: "No data",
            detail: "No movement records found.",
          });
          onSearch([], 0);
        }
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch movement logs or summary.",
        });
        onSearch([], 0);
      } finally {
        setLoading(false);
      }
    };

    const handleStartDateChange = (date: Date | null) => {
      setStartDate(date);
      onDateRangeChange(date, endDate); // 👈 notify parent
    };

    const handleEndDateChange = (date: Date | null) => {
      setEndDate(date);
      onDateRangeChange(startDate, date); // 👈 notify parent
    };

    const handleReset = () => {
      if (posList.length === 1) {
        setSelectedPosId(posList[0].posID);
      } else {
        setSelectedPosId(null);
      }
      setStartDate(null);
      setEndDate(null);
       onDateRangeChange(null, null);
      onSearch([], 0);
    };
    
    useImperativeHandle(ref, () => ({
      search: handleSearch,
    }));

    return (
      <div className="card border-noround p-3 w-full">
        <Toast ref={toast} />

        <div className="grid">
          {/* POS Dropdown */}
          <div className="col-12 md:col-2">
            <Dropdown
              value={selectedPosId}
              options={posList}
              optionLabel="posCode"
              optionValue="posID"
              placeholder="Select POS"
              onChange={(e: DropdownChangeEvent) => setSelectedPosId(e.value)}
              className="w-full"
              disabled={posList.length === 1}
            />
          </div>

          {/* Start Date */}
          <div className="col-6 md:col-2">
            <Calendar
              value={startDate}
              onChange={(e) => handleStartDateChange(e.value as Date | null)}
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="Start Date"
              className="w-full"
              readOnlyInput
            />
          </div>

          {/* End Date */}
          <div className="col-6 md:col-2">
            <Calendar
              value={endDate}
              onChange={(e) => handleEndDateChange(e.value as Date | null)}
              showButtonBar
              dateFormat="dd/mm/yy"
              placeholder="End Date"
              className="w-full"
              readOnlyInput
            />
          </div>

          {/* Filter + Reset Buttons */}
          <div className="col-6 md:col-2">
            <Button label="Filter" onClick={handleSearch} severity="success" className="w-full" />
          </div>
          <div className="col-6 md:col-2">
            <Button
              label="Reset"
              onClick={handleReset}
              severity="secondary"
              className="w-full"
              text
              raised
            />
          </div>
        </div>
      </div>
    );
  }
);

export default OutwardChallanSearch;
